import React, { useState, useEffect, useRef } from 'react';
import { AI_LAUNCHER, AI_PANEL_BOTTOM } from '../constants/adminChrome';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const CACHE_KEY = 'doorly_ai_briefing_cache';
const CACHE_HOURS = 4;

export default function AdminAIAssistant() {
  const [open, setOpen] = useState(false);
  const [briefings, setBriefings] = useState(null);   // [{ key, label, briefing|error }]
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const panelRef = useRef(null);

  const token = localStorage.getItem('doorly_admin_token');
  // The launcher badge counts bundles that came back with content, not borrower
  // counters — those only exist for one bundle and a partner_success user has
  // none of them.
  const alertCount = (briefings || []).filter((b) => b.briefing).length;

  // Load from cache on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { briefings: b, fetchedAt } = JSON.parse(cached);
        // ⚠️ SHAPE-CHECKED, NOT JUST AGE-CHECKED. The payload went from one
        // briefing to an array of them; a cache written by the old build would
        // otherwise deserialise into a render that expects tabs.
        if (Array.isArray(b) && (Date.now() - fetchedAt) / 3600000 < CACHE_HOURS) {
          setBriefings(b); setLastFetched(new Date(fetchedAt));
        }
      }
    } catch (_) {}
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const fetchBriefing = async (force = false) => {
    if (!force && briefings && lastFetched && (Date.now() - lastFetched.getTime()) / 3600000 < CACHE_HOURS) return;
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API}/api/admin/ai-briefing`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate briefing');
      setBriefings(data.briefings || []); setActiveTab(0);
      const now = new Date(); setLastFetched(now);
      localStorage.setItem(CACHE_KEY, JSON.stringify({ briefings: data.briefings || [], fetchedAt: now.getTime() }));
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  // Auto-fetch when panel opens
  useEffect(() => { if (open && !briefings && !loading) fetchBriefing(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatBriefing = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    const totalLines = lines.filter(l => l.trim()).length;
    return lines.map((line, i) => {
      if (!line.trim()) return <div key={i} style={{ height: '6px' }} />;
      if (/^[A-Z].*:$/.test(line.trim()) || /^[^\w\s]/.test(line.trim())) {
        return <div key={i} style={{ fontSize: '13px', fontWeight: '800', color: '#111827', margin: '14px 0 4px', paddingBottom: '4px', borderBottom: '1px solid #f3f4f6' }}>{line}</div>;
      }
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        return <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '5px', fontSize: '13px', color: '#374151', lineHeight: '1.6' }}><span style={{ color: '#3a6ad9', flexShrink: 0 }}>→</span><span>{line.replace(/^[-•]\s*/, '')}</span></div>;
      }
      const isLast = i === lines.length - 1 || (i === lines.length - 2 && !lines[lines.length - 1].trim());
      if (isLast && totalLines > 3) {
        return <div key={i} style={{ marginTop: '14px', padding: '10px 14px', background: '#3a6ad9', borderRadius: '10px', fontSize: '13px', fontWeight: '700', color: '#fff', lineHeight: '1.5' }}>{line.replace(/\*\*/g, '')}</div>;
      }
      return <div key={i} style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', marginBottom: '4px' }}>{line}</div>;
    });
  };

  return (
    <>
      {/* ── LAUNCHER — ONE FLOATING ELEMENT, TWO VISIBLE TARGETS ─────────────
          ⚠️ THIS REPLACES TWO SEPARATE FLOATING BUTTONS. ReportIssue used to
          own its own, and its comment records that it had already been moved
          once to avoid colliding with Eva's launcher — two controls competing
          for the same corner.

          ⚠️ THE SECOND SEGMENT IS THE REACH, AND IT IS DELIBERATELY VISIBLE.
          Folding report into the panel footer alone would mean opening the
          debrief before you could report a defect, and ReportIssue's own note
          argues it must be reachable AT the moment someone hits one. A hidden
          gesture (right-click / long-press / keyboard chord) keeps that on
          paper and loses it in practice — a control you must already know
          about is not available to the person who just hit the defect. So the
          bug segment sits on the launcher: one tap, from any admin page, and
          the panel never renders. */}
      <div style={{ position: 'fixed',
        // 🔑 FROM constants/adminChrome.js, which is also what a page reserving
        // clearance in this corner reads. These were literals here and nowhere
        // else, so no other file could know how much room the pill takes — and
        // the Messages inbox put its Send Reply button underneath it.
        bottom: `calc(${AI_LAUNCHER.bottom}px + env(safe-area-inset-bottom))`,
        right: `max(${AI_LAUNCHER.right}px, env(safe-area-inset-right))`,
        zIndex: 999, display: 'flex', alignItems: 'center', background: '#3a6ad9',
        borderRadius: '999px', border: '2px solid #fff', boxShadow: '0 4px 20px rgba(58,106,217,0.35)' }}>
        <button onClick={() => setOpen(o => !o)} title="Daily debrief"
          style={{ width: '52px', height: '52px', borderRadius: '999px 0 0 999px', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', padding: 0, lineHeight: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
          </svg>
          {alertCount > 0 && (
            <div style={{ position: 'absolute', top: '-2px', left: '-2px', width: '18px', height: '18px', borderRadius: '50%', background: '#ff082f', border: '2px solid #fff', fontSize: '10px', fontWeight: '800', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {alertCount > 9 ? '9+' : alertCount}
            </div>
          )}
        </button>
        <div style={{ width: 1, height: 26, background: 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
        {/* ⚠️ THE TWO ICONS SHARE A BASELINE, AND IT TOOK MORE THAN centering
            EACH BUTTON. Both were already flex-centred, but the SVGs differed:
            the star is 22px and visually centred in its viewBox, the bug glyph
            was 18px with antennae reaching y=2 and legs to y=18, so its optical
            centre sat low. Matching the box size and giving the bug path the
            same 24-unit viewBox extent puts the two on one line. */}
        <button onClick={() => window.dispatchEvent(new CustomEvent('doorly:report', { detail: { kind: 'bug' } }))}
          title="Report a bug"
          aria-label="Report a bug"
          style={{ width: '46px', height: '52px', borderRadius: '0 999px 999px 0', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 0 }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
            {/* Centred on (12,12) like the star: body 6→20, antennae and legs
                symmetric about it. */}
            <path d="M9 5.5L7.5 3.5M15 5.5l1.5-2M12 19.5v-8M6.5 11.5h11M7 9.5a5 5 0 0110 0v4.5a5 5 0 01-10 0zM4 12.5h3M17 12.5h3M5 16.5l2-1.2M19 16.5l-2-1.2"/>
          </svg>
        </button>
      </div>

      {/* Panel */}
      {open && (
        /* ⚠️ EXPRESSION POSITION, SO A BARE BLOCK COMMENT — NOT {/* … *​/}.
            This sits inside `{open && ( … )}`, which is an expression, not JSX
            children: there `{/* … *​/}` parses as an empty object literal
            followed by an element and fails with "Unexpected token, expected
            ','". The tab comment below is in the same position for the same
            reason.

            ⚠️ 380px FIXED WITH right:24 OVERFLOWED A 320px VIEWPORT — 404px of
            content in a 320px window, so the panel hung off the left edge before
            any of the sticky work below. Width is now a min() against the
            viewport, and the insets respect the safe area: on a notched device
            in landscape, right:24 sits under the rounded corner.

            ⚠️ maxHeight USES svh WITH A vh FALLBACK. On mobile Safari, vh is the
            LARGEST viewport (chrome hidden), so 70vh of a panel pinned to the
            bottom is taller than the visible area whenever the toolbar is
            showing — the footer, which is where the report actions now live,
            ends up under it. */
        <div ref={panelRef} style={{
          position: 'fixed',
          // ⚠️ DERIVED, not the literal 88 it was. That number was this same
          // arithmetic — the pill's offset plus its height plus a gap — written
          // out by hand in the one place that needed it.
          bottom: `calc(${AI_PANEL_BOTTOM}px + env(safe-area-inset-bottom))`,
          right: 'max(16px, env(safe-area-inset-right))',
          width: 'min(380px, calc(100vw - 32px))',
          maxHeight: '70vh', maxBlockSize: '70svh',
          background: '#fff', borderRadius: '20px', border: '1px solid #e5e7eb',
          boxShadow: '0 8px 40px rgba(0,0,0,0.18)', zIndex: 998,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>

          {/* Header */}
          <div style={{ background: '#3a6ad9', padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: '800', color: '#fff', letterSpacing: '-0.2px' }}>DoorlyDesk AI</div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
                {lastFetched ? 'Updated ' + lastFetched.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'Daily action briefing'}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => fetchBriefing(true)} disabled={loading} title="Refresh"
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px', padding: '6px 8px', cursor: loading ? 'not-allowed' : 'pointer', color: loading ? 'rgba(255,255,255,0.3)' : '#fff', fontSize: '14px', lineHeight: 1 }}>
                ↺
              </button>
              <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: '18px', lineHeight: 1, padding: '4px' }}>x</button>
            </div>
          </div>

          {/* ⚠️ THE META PILL ROW IS REMOVED, AND IT IS A BOUNDARY FIX RATHER
              THAN A TIDY-UP. It rendered active_borrowers / open_conditions /
              stuck_borrowers — borrower counts, straight from the seven
              statements that used to run for everyone. Under role-scoped
              bundles those numbers only exist for the borrower bundle, so
              keeping them would have leaked borrower aggregates to a
              partner_success user through the one part of the panel that never
              looked at which bundle it was rendering.

              Reinstating them per-bundle would mean each bundle exporting its
              own stat set — worth doing, but it is a feature rather than a
              carry-over, and the counts are already in the debrief text. */}

          {/* ⚠️ ONE BUNDLE RENDERS AS A PLAIN BRIEFING, NOT A LONE TAB. A
              client_success-only user is entitled to exactly one bundle, and a
              single-item tabset reads as a broken control — it invites the
              question "where are the others?" and the honest answer is that
              there are none to see. Tabs appear only when there is a choice to
              make. */}

          {/* ⚠️ THE TABS ARE NO LONGER STICKY — THEY ARE OUTSIDE THE SCROLLER
              ENTIRELY, AND THAT IS THE FIX. As a sticky child of the padded
              body, the briefing text rendered ABOVE the bar as it scrolled: the
              negative margins made the row start flush and span the full width,
              but they do not move where `top: 0` PINS it, and a sticky box is
              still a child of the thing scrolling past it. Adding opacity and
              z-index would have hidden the symptom at the bar itself while
              leaving a live strip above it — which is the version of this fix
              that looks right in the stylesheet and is still wrong on screen.

              As a flex sibling of the header and the body, the bar cannot be
              scrolled past, cannot be overlapped, and needs no z-index: the
              content it sits above is inside a DIFFERENT box, which clips its
              own overflow. "Frozen" is now structural rather than declared. */}
          {!loading && !error && briefings && briefings.length > 1 && (
            <div className="dd-tabs" style={{
              display: 'flex', gap: 2, flexShrink: 0,
              padding: '10px 12px 0',
              borderBottom: '1px solid #e5e7eb',
              overflowX: 'auto', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none',
            }}>
              {/* ⚠️ AND IF IT EVER DOES OVERFLOW, IT LOOKS LIKE IT. The row used
                  to scroll silently, so "Engineering Queue" was simply cut off
                  at the right margin with nothing to say more existed — it read
                  as broken, not as scrollable. These are the pure-CSS scroll
                  shadows: the white covers scroll WITH the content (`local`),
                  so a shadow is exposed at an edge only while there is more
                  content past it, and disappears once you reach the end. No
                  measurement, no resize listener, nothing to fall out of sync.

                  Short labels mean this should not trigger at all now — it is
                  here so the next label longer than the panel degrades into
                  something scrollable rather than something truncated. */}
              <style>{`
                .dd-tabs {
                  background-color: #fff;
                  background-image:
                    linear-gradient(to right, #fff 40%, rgba(255,255,255,0)),
                    linear-gradient(to left,  #fff 40%, rgba(255,255,255,0)),
                    linear-gradient(to right, rgba(0,0,0,0.13), rgba(0,0,0,0)),
                    linear-gradient(to left,  rgba(0,0,0,0.13), rgba(0,0,0,0));
                  background-position: left center, right center, left center, right center;
                  background-repeat: no-repeat;
                  background-size: 24px 100%, 24px 100%, 12px 100%, 12px 100%;
                  background-attachment: local, local, scroll, scroll;
                }
                .dd-tabs::-webkit-scrollbar { display: none; }
              `}</style>
              {briefings.map((b, i) => (
                /* ⚠️ THEY LOOK LIKE TABS NOW, not like three words with a line
                   under one of them. The selected tab is a raised card joined to
                   the panel below — its bottom border is the SAME colour as its
                   own background, so it visually cuts the divider and reads as
                   the front of a stack. An underline alone reads as a link, and
                   at 12px grey the unselected ones read as a caption. */
                <button key={b.key} onClick={() => setActiveTab(i)}
                  style={{
                    padding: '9px 14px', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer',
                    whiteSpace: 'nowrap', flexShrink: 0, marginBottom: '-1px',
                    borderTopLeftRadius: 8, borderTopRightRadius: 8,
                    background: i === activeTab ? '#fff' : 'transparent',
                    color: i === activeTab ? '#3a6ad9' : '#6b7280',
                    border: i === activeTab ? '1px solid #e5e7eb' : '1px solid transparent',
                    borderBottomColor: i === activeTab ? '#fff' : 'transparent',
                  }}>
                  {/* ⚠️ short WITH A FALLBACK, because the backend and the
                      frontend deploy separately — between the two the API is
                      still sending only `label`, and a missing short label must
                      degrade to the long one rather than to nothing. */}
                  {b.short || b.label}
                </button>
              ))}
            </div>
          )}


          {/* Body */}
          <div style={{ overflowY: 'auto', padding: '16px 20px', flex: 1 }}>
            {loading && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid #f3f4f6', borderTop: '3px solid #3a6ad9', borderRadius: '50%', animation: 'aispin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <div style={{ fontSize: '13px', color: '#9ca3af' }}>Analyzing your pipeline...</div>
                <style>{`@keyframes aispin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}
            {error && (
              <div style={{ padding: '12px 14px', background: '#fff0f3', border: '1px solid #ffc5ca', borderRadius: '10px', fontSize: '13px', color: '#ff082f' }}>
                {error}
                <button onClick={() => fetchBriefing(true)} style={{ display: 'block', marginTop: '8px', background: 'none', border: 'none', color: '#3a6ad9', cursor: 'pointer', fontSize: '13px', padding: 0, textDecoration: 'underline' }}>Try again</button>
              </div>
            )}
            {!loading && !error && briefings && briefings.length > 0 && (() => {
              const b = briefings[Math.min(activeTab, briefings.length - 1)];
              if (b.error) {
                return <div style={{ fontSize: '13px', color: '#ff082f' }}>{b.label}: {b.error}</div>;
              }
              return (
                <>
                  {/* With one bundle the label still appears, quietly — it says
                      WHAT this debrief covers, which is the thing a tab would
                      otherwise have communicated. */}
                  {briefings.length === 1 && (
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 }}>
                      {b.label}
                    </div>
                  )}
                  {formatBriefing(b.briefing)}
                </>
              );
            })()}
            {/* ⚠️ AN EMPTY ARRAY IS NOT AN ERROR. A role mapped to no bundle
                gets nothing rather than the borrower debrief — see
                knowledge/briefingBundles.js. Saying so is better than an empty
                panel that looks broken. */}
            {!loading && !error && briefings && briefings.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', fontSize: '13px', color: '#9ca3af', lineHeight: 1.6 }}>
                There's no debrief configured for your role yet.
              </div>
            )}
            {!loading && !error && !briefings && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#111827', marginBottom: '4px' }}>Your AI briefing is ready</div>
                <div style={{ fontSize: '13px', color: '#9ca3af', marginBottom: '16px' }}>Get a prioritized summary of actions needed today.</div>
                <button onClick={() => fetchBriefing(true)}
                  style={{ padding: '10px 24px', background: '#3a6ad9', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '800' }}>
                  Generate Briefing
                </button>
              </div>
            )}
          </div>

          {/* ── Footer — the report actions live here ─────────────────────
              Both kinds, because a feature request has no launcher segment of
              its own: the launcher's second target is bug-only, on the argument
              that "something is broken right now" is the case that cannot wait
              for a panel to open. Wanting something better can. */}
          <div style={{ padding: '10px 20px', borderTop: '1px solid #f3f4f6', background: '#f9fafb', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={() => window.dispatchEvent(new CustomEvent('doorly:report', { detail: { kind: 'bug' } }))}
                style={footerLink}>Report a bug</button>
              <button onClick={() => window.dispatchEvent(new CustomEvent('doorly:report', { detail: { kind: 'feature' } }))}
                style={footerLink}>Request a feature</button>
            </div>
            <button onClick={() => fetchBriefing(true)} disabled={loading}
              style={{ ...footerLink, opacity: loading ? 0.4 : 1 }}>
              Refresh
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const footerLink = { fontSize: '11px', color: '#3a6ad9', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' };
